// @file Audio-recorder-specific state for the combined AV recorder.
// Extracted from useContentPickerAvRecorderStore to keep concerns separated.
// Dependencies: useGlobalContentPickerStore, useGlobalConfirmationDialogStore (no dependency on the AV store).

import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { ContentPickerSource } from '@@/bits/content_picker'
import { AudioRecorderStatus, type AudioRecorderPickerState } from '@@/bits/content_picker_configs'
import { __ } from '@@/bits/intl'
import { unboundedWatch } from '@@/bits/vue'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalContentPickerStore } from '@@/pinia/useGlobalContentPickerStore'
import { defineStore } from 'pinia'
import { computed } from 'vue'

const useContentPickerAvAudioRecorderStore = defineStore('contentPickerAvAudioRecorderStore', () => {
  const globalStore = useGlobalContentPickerStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()

  // #region Audio recorder state

  function setAudioRecorderStatus(value: AudioRecorderStatus): void {
    globalStore.setPickerState(ContentPickerSource.AudioRecorder, {
      source: ContentPickerSource.AudioRecorder,
      audioRecorderStatus: value,
    })
  }

  const audioRecorderStatus = computed<AudioRecorderStatus>(() => {
    const state = globalStore.currentSession?.pickerStates[ContentPickerSource.AudioRecorder]
    if (state != null && 'audioRecorderStatus' in state) {
      return (state as AudioRecorderPickerState).audioRecorderStatus
    }
    return AudioRecorderStatus.Connecting
  })

  const hasUnsavedAudioWork = computed<boolean>(() => {
    const s = audioRecorderStatus.value
    return (
      s === AudioRecorderStatus.Recording || s === AudioRecorderStatus.Paused || s === AudioRecorderStatus.Previewing
    )
  })

  const hasStartedRecordingAudio = computed<boolean>(() => {
    return hasUnsavedAudioWork.value
  })

  // #endregion Audio recorder state

  // #region Close guard

  function showDiscardDialog(): void {
    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      title: __('You have unsaved changes'),
      body: __('Are you sure you want to discard your recording?'),
      confirmButtonText: __('Discard'),
      cancelButtonText: __('Stay here'),
      afterConfirmActions: [() => setAudioRecorderStatus(AudioRecorderStatus.Streaming)],
      afterCancelActions: [],
      shouldFadeIn: false,
      forceFullWidthButtons: true,
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    })
  }

  // #endregion Close guard

  // #region Beforeunload
  // Watcher needed because the beforeunload listener must be managed at the store level
  // (not the component) to survive component unmount/remount cycles during recording.
  // A computed property cannot produce side effects (adding/removing event listeners).

  const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (hasUnsavedAudioWork.value) {
      event.preventDefault()
    }
  }

  unboundedWatch(hasUnsavedAudioWork, (hasWork) => {
    if (hasWork) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  })

  // #endregion Beforeunload

  return {
    audioRecorderStatus,
    setAudioRecorderStatus,
    hasUnsavedAudioWork,
    hasStartedRecordingAudio,
    showDiscardDialog,
  }
})

export { useContentPickerAvAudioRecorderStore }
